/**
 * Address helpers — formatting + map deeplink generation.
 *
 * Two naming conventions exist in the schema (recon documented in Session 34):
 *   - address_* prefix: contacts, field_jobs
 *   - bare names: locations, vendors
 *
 * The exported `fromAddressPrefix` / `fromBareNames` adapters normalize either
 * shape into the internal `AddressParts` so callers don't have to map by hand.
 *
 * Empty/null/whitespace parts are filtered; if everything is empty the helpers
 * return "" / null so callers can hide affordances cleanly.
 */

export interface AddressParts {
  line1?: string | null
  line2?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  country?: string | null
}

export interface FormatOpts {
  /** Append country as a final segment when present. Default false. */
  includeCountry?: boolean
  /** Render with newlines between segments instead of commas. Default false. */
  multiline?: boolean
}

/** address_* prefix shape (contacts, field_jobs). */
export function fromAddressPrefix(row: {
  address_line1?: string | null
  address_line2?: string | null
  address_city?: string | null
  address_state?: string | null
  address_zip?: string | null
  address_country?: string | null
}): AddressParts {
  return {
    line1: row.address_line1,
    line2: row.address_line2,
    city: row.address_city,
    state: row.address_state,
    zip: row.address_zip,
    country: row.address_country,
  }
}

/** Bare-name shape (locations, vendors). line1/line2 still use the address_ prefix. */
export function fromBareNames(row: {
  address_line1?: string | null
  address_line2?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  country?: string | null
}): AddressParts {
  return {
    line1: row.address_line1,
    line2: row.address_line2,
    city: row.city,
    state: row.state,
    zip: row.zip,
    country: row.country,
  }
}

function clean(s: string | null | undefined): string {
  return (s ?? '').trim()
}

/**
 * Compose "city, state zip" with the right punctuation.
 * Returns empty string if all three are empty.
 */
function joinCityStateZip(city: string, state: string, zip: string): string {
  const right = [state, zip].filter(Boolean).join(' ')
  return [city, right].filter(Boolean).join(', ')
}

/**
 * Human-readable address string. Empty parts are filtered; if every part is
 * empty/null, returns "". Single-line default keeps drop-in compatibility with
 * the inline patterns we're replacing in FieldHub.
 *
 * Single-line example: "123 Main St, Apt 4, Dallas, TX 75201"
 * Multi-line example:  "123 Main St\nApt 4\nDallas, TX 75201"
 */
export function formatAddress(parts: AddressParts, opts: FormatOpts = {}): string {
  const line1 = clean(parts.line1)
  const line2 = clean(parts.line2)
  const city = clean(parts.city)
  const state = clean(parts.state)
  const zip = clean(parts.zip)
  const country = clean(parts.country)

  const cityLine = joinCityStateZip(city, state, zip)

  const segments: string[] = []
  if (line1) segments.push(line1)
  if (line2) segments.push(line2)
  if (cityLine) segments.push(cityLine)
  if (opts.includeCountry && country) segments.push(country)

  if (segments.length === 0) return ''
  return opts.multiline ? segments.join('\n') : segments.join(', ')
}

/**
 * Google Maps deeplink. Always includes country when present for higher
 * geocoding precision. Returns null when the address is empty so callers can
 * hide the link button cleanly.
 */
export function mapsUrl(parts: AddressParts): string | null {
  const query = formatAddress(parts, { includeCountry: true })
  if (!query) return null
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
}
